<template>
    <DialogLayout :class-name="'terminate-interview-dialog'" @close="handleClose">
        <template #title>{{ t('candidate.confirmTerminateSelection') }}</template>
        <template #default>
            <div class="terminate-dialog-content">
                <div class="confirm-message">
                    {{ t('candidate.terminateSelectionConfirmMessage', { interviewId: props.interviewId }) }}
                </div>
                <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'" class="terminate-form">
                    <el-form-item :label="t('candidate.terminationReason')" prop="terminateCode">
                        <el-select v-model="formData.terminateCode" :placeholder="t('common.pleaseSelect')" style="width: 100%">
                            <el-option
                                v-for="item in reasonOptions"
                                :key="item.value"
                                :label="item.label"
                                :value="item.value"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item :label="t('candidate.additionalNote')" prop="reason">
                        <el-input v-model="formData.reason" type="textarea" :placeholder="t('common.pleaseEnter')"
                            :rows="4" maxlength="200" show-word-limit />
                    </el-form-item>
                </el-form>
            </div>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="danger" class="terminate-btn" :loading="submitting" @click="handleConfirm">
                {{ t('candidate.terminate') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { TerminateInterviewDialogProps } from './terminate-interview-dialog-types'
import { terminateInterviewApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<TerminateInterviewDialogProps>()

const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const submitting = ref(false)
const formRef = ref<FormInstance | null>(null)

// 终止原因选项
const reasonOptions = computed(() => [
    {
        label: t('candidate.terminationReasonActiveExit'),
        value: 1,
    },
    {
        label: t('candidate.terminationReasonOther'),
        value: 2,
    },
])

// 表单数据
const formData = reactive({
    reason: null as number | null,
    additionalNote: '',
})

// 校验规则（两项必填）
const rules: FormRules = {
    terminateCode: [
        {
            required: true,
            message: t('candidate.pleaseSelectTerminationReason'),
            trigger: 'blur',
        },
    ],
    reason: [
        {
            required: true,
            message: t('candidate.pleaseEnterAdditionalNote'),
            trigger: 'blur',
        },
    ],
}

// 确认终止
const handleConfirm = async () => {
    if (!formRef.value) return

    await formRef.value.validate(async (valid) => {
        if (!valid) return

        submitting.value = true
        try {
            await terminateInterviewApi({
                interviewId: props.interviewId,
                reason: formData.reason,
                terminateCode: formData.terminateCode,
            } as any)

            submitting.value = false
            emit('confirm')
        } catch (error: any) {
            submitting.value = false
            ElMessage.error(error?.message || 'Error')
        }
    })
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.terminate-dialog-content {
    .confirm-message {
        font-size: 14px;
        color: #495057;
        line-height: 22px;
        margin-bottom: 24px;
    }
}

.terminate-form {
    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

</style>

<style lang="scss">
.terminate-interview-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
.terminate-btn {
     border-color: #F03D3E !important;

     &:hover {
         background-color: #F03D3E !important;
         border-color: #F03D3E !important;
         color: #FFFFFF !important;
     }

     &:active {
         background-color: #D32F2F !important;
         border-color: #D32F2F !important;
         color: #FFFFFF !important;
     }
}
</style>

